import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Heading,
  Input,
  Textarea,
  Button,
  IconButton,
  Badge,
  Divider,
  FormControl,
  FormLabel,
  Spinner,
  useToast,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Icon,
} from '@chakra-ui/react';
import { FiEdit3, FiCheck, FiX, FiUpload, FiTrash2, FiPlus, FiFile } from 'react-icons/fi';
import api from '../../services/api';

const inputProps = {
  bg: '#171717',
  borderColor: 'rgba(255,255,255,0.12)',
  color: '#fafafa',
  fontSize: 'sm',
  _placeholder: { color: 'rgba(255,255,255,0.25)' },
  _hover: { borderColor: 'rgba(255,255,255,0.2)' },
  _focus: { borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' },
};

const labelProps = {
  fontSize: 'xs',
  color: '#a3a3a3',
  mb: 1,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function TagInput({ values = [], onChange, placeholder }) {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (item) => {
    onChange(values.filter((v) => v !== item));
  };

  return (
    <Box>
      <Wrap spacing={1} mb={2}>
        {values.map((item) => (
          <WrapItem key={item}>
            <Tag size="sm" bg="rgba(255,255,255,0.1)" color="#fafafa" borderRadius="full">
              <TagLabel>{item}</TagLabel>
              <TagCloseButton onClick={() => handleRemove(item)} />
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
      <HStack spacing={2}>
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Type and press Enter'}
          size="sm"
          {...inputProps}
        />
        <IconButton
          icon={<FiPlus />}
          size="sm"
          variant="ghost"
          color="#a3a3a3"
          onClick={handleAdd}
          _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.08)' }}
          _focus={{ boxShadow: 'none' }}
          aria-label="Add item"
        />
      </HStack>
    </Box>
  );
}

const SellerProfileCard = forwardRef(function SellerProfileCard(
  { profile, onUpdate, firstName = '', lastName = '', hideSaveButton = false },
  ref
) {
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    company_name: profile?.company_name || '',
    company_summary: profile?.company_summary || '',
    alma_mater: profile?.alma_mater || '',
    interests: profile?.interests || [],
    clubs_associations: profile?.clubs_associations || [],
    area_of_expertise: profile?.area_of_expertise || '',
    location: profile?.location || '',
    free_form_context: profile?.free_form_context || '',
  });

  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [contextFiles, setContextFiles] = useState(profile?.context_files || []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/seller-profile/', form);
      onUpdate?.(data);
      toast({
        title: 'Profile saved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Expose save() so parent components (e.g. modals) can trigger it without
  // showing the built-in save button.
  useImperativeHandle(ref, () => ({ save: handleSave }));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['.txt', '.md'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) {
      toast({
        title: 'Invalid file type',
        description: 'Only .txt and .md files are allowed.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/api/seller-profile/upload-context-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setContextFiles(data.context_files || []);
      onUpdate?.(data);
      toast({ title: 'File uploaded', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      const { data } = await api.delete(`/api/seller-profile/context-file/${fileId}`);
      setContextFiles(data.context_files || []);
      onUpdate?.(data);
      toast({ title: 'File removed', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    <Box
      bg="#0f0f0f"
      border="1px solid rgba(255,255,255,0.10)"
      borderRadius="12px"
      p={6}
      w="100%"
    >
      <VStack spacing={6} align="stretch">
        {/* Identity header */}
        {fullName && (
          <Box>
            <Heading size="sm" color="#fafafa" fontWeight="600" letterSpacing="-0.01em">
              {fullName}
            </Heading>
            {form.location && (
              <Text fontSize="xs" color="#a3a3a3" mt={0.5}>
                {form.location}
              </Text>
            )}
          </Box>
        )}

        <Divider borderColor="rgba(255,255,255,0.08)" />

        {/* Company info */}
        <VStack spacing={4} align="stretch">
          <Text fontSize="xs" color="#737373" fontWeight="600" textTransform="uppercase" letterSpacing="0.08em">
            Company
          </Text>

          <FormControl>
            <FormLabel {...labelProps}>Company Name</FormLabel>
            <Input
              value={form.company_name}
              onChange={(e) => handleChange('company_name', e.target.value)}
              placeholder="Acme Corp"
              size="sm"
              {...inputProps}
            />
          </FormControl>

          <FormControl>
            <FormLabel {...labelProps}>Company Summary</FormLabel>
            <Textarea
              value={form.company_summary}
              onChange={(e) => handleChange('company_summary', e.target.value)}
              placeholder="2-4 sentence summary of what your company does..."
              size="sm"
              rows={3}
              resize="vertical"
              {...inputProps}
            />
          </FormControl>
        </VStack>

        <Divider borderColor="rgba(255,255,255,0.08)" />

        {/* Personal / LinkedIn info */}
        <VStack spacing={4} align="stretch">
          <Text fontSize="xs" color="#737373" fontWeight="600" textTransform="uppercase" letterSpacing="0.08em">
            Background
          </Text>

          <Flex gap={4} flexWrap="wrap">
            <FormControl flex="1" minW="180px">
              <FormLabel {...labelProps}>Alma Mater</FormLabel>
              <Input
                value={form.alma_mater}
                onChange={(e) => handleChange('alma_mater', e.target.value)}
                placeholder="University of Colorado"
                size="sm"
                {...inputProps}
              />
            </FormControl>
            <FormControl flex="1" minW="180px">
              <FormLabel {...labelProps}>Location</FormLabel>
              <Input
                value={form.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Denver, CO"
                size="sm"
                {...inputProps}
              />
            </FormControl>
          </Flex>

          <FormControl>
            <FormLabel {...labelProps}>Area of Expertise</FormLabel>
            <Textarea
              value={form.area_of_expertise}
              onChange={(e) => handleChange('area_of_expertise', e.target.value)}
              placeholder="e.g. Wealth management and financial planning for high-net-worth individuals"
              size="sm"
              rows={2}
              resize="vertical"
              {...inputProps}
            />
          </FormControl>

          <FormControl>
            <FormLabel {...labelProps}>Interests</FormLabel>
            <TagInput
              values={form.interests}
              onChange={(v) => handleChange('interests', v)}
              placeholder="e.g. Golf, Fly fishing — press Enter to add"
            />
          </FormControl>

          <FormControl>
            <FormLabel {...labelProps}>Clubs & Associations</FormLabel>
            <TagInput
              values={form.clubs_associations}
              onChange={(v) => handleChange('clubs_associations', v)}
              placeholder="e.g. Rotary Club, CFA Society — press Enter to add"
            />
          </FormControl>
        </VStack>

        <Divider borderColor="rgba(255,255,255,0.08)" />

        {/* Free-form context */}
        <VStack spacing={4} align="stretch">
          <Text fontSize="xs" color="#737373" fontWeight="600" textTransform="uppercase" letterSpacing="0.08em">
            Additional Context
          </Text>
          <FormControl>
            <FormLabel {...labelProps}>Free-form Context</FormLabel>
            <Textarea
              value={form.free_form_context}
              onChange={(e) => handleChange('free_form_context', e.target.value)}
              placeholder="Any additional context you'd like the AI to know about you or your business when generating outreach..."
              size="sm"
              rows={4}
              resize="vertical"
              {...inputProps}
            />
          </FormControl>
        </VStack>

        <Divider borderColor="rgba(255,255,255,0.08)" />

        {/* Context file uploads */}
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Text fontSize="xs" color="#737373" fontWeight="600" textTransform="uppercase" letterSpacing="0.08em">
              Context Files
            </Text>
            <Button
              size="xs"
              variant="ghost"
              color="#a3a3a3"
              leftIcon={uploadingFile ? <Spinner size="xs" /> : <Icon as={FiUpload} />}
              onClick={() => fileInputRef.current?.click()}
              isDisabled={uploadingFile}
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.08)' }}
              _focus={{ boxShadow: 'none' }}
            >
              {uploadingFile ? 'Uploading...' : 'Upload .txt / .md'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.md"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
          </HStack>

          {contextFiles.length === 0 ? (
            <Text fontSize="xs" color="#525252">
              No files uploaded. Upload .txt or .md files to include additional context for the AI.
            </Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {contextFiles.map((f) => (
                <HStack
                  key={f.file_id}
                  bg="rgba(255,255,255,0.04)"
                  border="1px solid rgba(255,255,255,0.08)"
                  borderRadius="8px"
                  px={3}
                  py={2}
                  justify="space-between"
                >
                  <HStack spacing={2}>
                    <Icon as={FiFile} color="#a3a3a3" boxSize={3.5} />
                    <Text fontSize="xs" color="#d4d4d4">
                      {f.filename}
                    </Text>
                    <Badge
                      fontSize="9px"
                      colorScheme="gray"
                      bg="rgba(255,255,255,0.08)"
                      color="#737373"
                    >
                      {Math.round(f.content.length / 1024 * 10) / 10} KB
                    </Badge>
                  </HStack>
                  <IconButton
                    icon={<FiTrash2 />}
                    size="xs"
                    variant="ghost"
                    color="#525252"
                    onClick={() => handleDeleteFile(f.file_id)}
                    _hover={{ color: '#ef4444', bg: 'transparent' }}
                    _focus={{ boxShadow: 'none' }}
                    aria-label="Remove file"
                  />
                </HStack>
              ))}
            </VStack>
          )}
        </VStack>

        {/* Save button — hidden when parent controls saving (e.g. modal flow) */}
        {!hideSaveButton && (
          <Button
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving..."
            bg="#fafafa"
            color="#0a0a0a"
            borderRadius="full"
            fontWeight="600"
            fontSize="sm"
            h="38px"
            _hover={{ opacity: 0.85 }}
            _focus={{ boxShadow: 'none' }}
          >
            Save Profile
          </Button>
        )}
      </VStack>
    </Box>
  );
});

export default SellerProfileCard;
